// Chart-scoped controls for the tiered flow-of-funds Sankey.
//
// Layout: a compact top toolbar (quick-perspective preset + the "Show each
// account separately" toggle) over a centered 7-tier column strip. The strip is
// centered so its Account card lines up on the chart's center axis, mirroring
// the columns of the Sankey below it. Pure presentation — all state is owned by
// the parent (TransactionAnalyzer). The Account column is always shown and is
// not a checkbox: it is the pivot every inflow ends at and every outflow starts
// from.
import { TIER_DEFS } from './sankey-utils.js';
import { perspectiveMap } from './report-sankey-analysis.js';
import styles from './sankey-chart-controls.module.css';

const INFLOW_TIERS = TIER_DEFS.filter(t => t.side === 'in');
const OUTFLOW_TIERS = TIER_DEFS.filter(t => t.side === 'out');

export default function SankeyChartControls({
  presetValue = 'CATEGORY',
  onPresetChange,
  enabledTiers,
  onToggleTier,
  separateAccounts = true,
  onToggleSeparate,
}) {
  const isOn = (key) => (enabledTiers && typeof enabledTiers.has === 'function' && enabledTiers.has(key));

  const tierCard = (t) => {
    const on = isOn(t.key);
    return (
      <label key={t.key} className={`${styles.tier} ${on ? styles.tierOn : ''}`}>
        <input
          type="checkbox"
          className={styles.checkbox}
          checked={on}
          onChange={() => onToggleTier?.(t.key)}
        />
        <span className={styles.side}>{t.side === 'in' ? 'Inflow' : 'Outflow'}</span>
        <span className={styles.name}>{t.name}</span>
      </label>
    );
  };

  return (
    <div className={styles.controls}>
      <div className={styles.tiersBlock}>
        <span className={styles.label}>Columns to show</span>
        <div className={styles.tiers}>
          <div className={`${styles.group} ${styles.inflow}`} role="group" aria-label="Inflow columns">
            {INFLOW_TIERS.map(tierCard)}
          </div>
          <div className={`${styles.tier} ${styles.account}`} aria-label="Account column (always shown)">
            <span className={styles.side}>Center</span>
            <span className={styles.name}>Account</span>
            <span className={styles.lock}>always shown</span>
          </div>
          <div className={`${styles.group} ${styles.outflow}`} role="group" aria-label="Outflow columns">
            {OUTFLOW_TIERS.map(tierCard)}
          </div>
        </div>
      </div>

      <div className={styles.toolbar}>
        <div className={styles.field}>
          <span className={styles.label} id="sankey-preset-label">Quick perspective</span>
          <select
            className={styles.select}
            aria-labelledby="sankey-preset-label"
            value={presetValue}
            onChange={(e) => onPresetChange?.(e.target.value)}
          >
            {Object.entries(perspectiveMap).map(([key, value]) => (
              <option key={key} value={key}>{value.label}</option>
            ))}
            {presetValue === 'CUSTOM' && <option value="CUSTOM">Custom</option>}
          </select>
        </div>

        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={separateAccounts}
            onChange={(e) => onToggleSeparate?.(e.target.checked)}
          />
          <span>Show each account separately</span>
        </label>
      </div>
    </div>
  );
}
